const focusableSelector = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]',
].join(',');

const keyboardTabbableSelector = [
  '.rmdp-day:not(.rmdp-day-hidden)',
  '.rmdp-left',
  '.rmdp-right',
  '.rmdp-arrow-container',
  '.rmdp-ym .rmdp-left',
  '.rmdp-ym .rmdp-right',
  '.rmdp-header-values span',
].join(',');

const keydownHandledAttribute = 'data-keydown-added';

const isVisible = (element: Element | null): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const hasLayout = Boolean(
    element.offsetWidth || element.offsetHeight || element.getClientRects().length,
  );
  if (!hasLayout) {
    return false;
  }

  const style = window.getComputedStyle(element);
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.visibility !== 'collapse'
  );
};

const getFocusableElements = (rootElement: Element) =>
  Array.from(rootElement.querySelectorAll<HTMLElement>(focusableSelector)).filter(
    (element) =>
      isVisible(element) &&
      !element.classList.contains('rmdp-disabled') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.getAttribute('aria-disabled') !== 'true' &&
      !element.hasAttribute('disabled'),
  );

const getActiveCalendarOverlay = () =>
  Array.from(
    document.querySelectorAll<HTMLElement>(
      '.rmdp-container .rmdp-wrapper, .rmdp-container .rmdp-calendar',
    ),
  ).find((overlayElement) => isVisible(overlayElement));

const getCalendarOverlayFromNode = (node: EventTarget | Node | null): HTMLElement | null => {
  if (!(node instanceof Element)) {
    return null;
  }
  const overlay = node.closest<HTMLElement>('.rmdp-wrapper, .rmdp-calendar');
  return overlay && isVisible(overlay) ? overlay : null;
};

const getFocusScopeRoot = (calendarOverlayElement: HTMLElement): HTMLElement =>
  calendarOverlayElement.closest<HTMLElement>('.rmdp-wrapper') ||
  calendarOverlayElement;

const focusFirstVisibleElement = (
  selector: string,
  parentElement: ParentNode = document,
) => {
  const firstElement = parentElement.querySelector<HTMLElement>(selector);
  if (firstElement && isVisible(firstElement)) {
    firstElement.focus();
  }
};

const activateDatePickerElement = (element: HTMLElement) => {
  const isHeaderValue =
    element.parentElement &&
    element.parentElement.classList.contains('rmdp-header-values');
  const isMonthOption = Boolean(element.closest('.rmdp-month-picker'));
  const isYearOption = Boolean(element.closest('.rmdp-year-picker'));

  element.click();

  if (isHeaderValue) {
    window.setTimeout(() => {
      if (!element.parentElement) {
        return;
      }
      const spans = element.parentElement.querySelectorAll('span');
      const spanIndex = Array.from(spans).indexOf(element);
      if (spanIndex === 0) {
        focusFirstVisibleElement(
          '.rmdp-month-picker .rmdp-day:not(.rmdp-day-hidden)',
        );
      } else if (spanIndex === 1) {
        focusFirstVisibleElement(
          '.rmdp-year-picker .rmdp-day:not(.rmdp-day-hidden)',
        );
      }
    }, 100);
    return;
  }

  if (isMonthOption || isYearOption) {
    window.setTimeout(() => {
      focusFirstVisibleElement('.rmdp-day:not(.rmdp-day-hidden)');
    }, 100);
  }
};

const onDatePickerElementKeyDown = (
  element: HTMLElement,
  event: KeyboardEvent,
) => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  activateDatePickerElement(element);
};

const ensureKeyboardTabbableElements = (
  calendarOverlayElement: HTMLElement,
) => {
  const focusScopeRoot = getFocusScopeRoot(calendarOverlayElement);
  const keyboardElements = Array.from(
    focusScopeRoot.querySelectorAll<HTMLElement>(keyboardTabbableSelector),
  );

  keyboardElements.forEach((element) => {
    if (element.classList.contains('rmdp-disabled')) {
      element.setAttribute('tabindex', '-1');
      return;
    }

    element.setAttribute('tabindex', '0');
    if (element.getAttribute(keydownHandledAttribute) !== 'true') {
      element.addEventListener('keydown', (event) => {
        onDatePickerElementKeyDown(element, event);
      });
      element.setAttribute(keydownHandledAttribute, 'true');
    }
  });
};

const getNextFocusableElement = (
  focusableElements: HTMLElement[],
  activeElement: HTMLElement | null,
  moveBackward: boolean,
) => {
  if (focusableElements.length === 0) {
    return null;
  }

  const currentIndex = activeElement ? focusableElements.indexOf(activeElement) : -1;
  if (currentIndex === -1) {
    return moveBackward
      ? focusableElements[focusableElements.length - 1]
      : focusableElements[0];
  }

  if (moveBackward) {
    return currentIndex === 0
      ? focusableElements[focusableElements.length - 1]
      : focusableElements[currentIndex - 1];
  }

  return currentIndex === focusableElements.length - 1
    ? focusableElements[0]
    : focusableElements[currentIndex + 1];
};

const getActivePickerPanel = (
  calendarOverlayElement: HTMLElement,
): HTMLElement | null => {
  const monthPicker =
    calendarOverlayElement.querySelector<HTMLElement>('.rmdp-month-picker');
  if (monthPicker && window.getComputedStyle(monthPicker).display === 'flex') {
    return monthPicker;
  }

  const yearPicker =
    calendarOverlayElement.querySelector<HTMLElement>('.rmdp-year-picker');
  if (yearPicker && window.getComputedStyle(yearPicker).display === 'block') {
    return yearPicker;
  }

  return calendarOverlayElement.querySelector<HTMLElement>('.rmdp-day-picker');
};

const getWeekSelectorInitialFocus = (
  calendarOverlayElement: HTMLElement,
): HTMLElement | null => {
  const overlayRoot = getFocusScopeRoot(calendarOverlayElement);

  const preferredElement = overlayRoot.querySelector<HTMLElement>(
    '[data-test="day-7"]',
  );
  if (
    preferredElement &&
    isVisible(preferredElement) &&
    !preferredElement.hasAttribute('disabled') &&
    preferredElement.getAttribute('aria-disabled') !== 'true'
  ) {
    return preferredElement;
  }

  const firstWeekSelectorButton = overlayRoot.querySelector<HTMLElement>(
    '[data-test="week-selector-plugin"] button:not([disabled])',
  );
  if (
    firstWeekSelectorButton &&
    isVisible(firstWeekSelectorButton) &&
    firstWeekSelectorButton.getAttribute('aria-disabled') !== 'true'
  ) {
    return firstWeekSelectorButton;
  }

  return null;
};

const focusFirstElementInActivePickerPanel = (
  calendarOverlayElement: HTMLElement,
) => {
  const panel = getActivePickerPanel(calendarOverlayElement);
  if (!panel) {
    return false;
  }

  const panelCandidates = Array.from(
    panel.querySelectorAll<HTMLElement>(
      '.rmdp-day:not(.rmdp-day-hidden), .rmdp-arrow-container, .rmdp-left, .rmdp-right, [tabindex]:not([tabindex="-1"])',
    ),
  );

  const firstFocusablePanelElement = panelCandidates.find(
    (element) =>
      isVisible(element) &&
      !element.classList.contains('rmdp-disabled') &&
      element.getAttribute('aria-disabled') !== 'true' &&
      !element.hasAttribute('disabled'),
  );

  if (firstFocusablePanelElement && isVisible(firstFocusablePanelElement)) {
    firstFocusablePanelElement.focus();
    return true;
  }

  return false;
};

const focusOverlay = (calendarOverlayElement: HTMLElement) => {
  const focusScopeRoot = getFocusScopeRoot(calendarOverlayElement);
  ensureKeyboardTabbableElements(calendarOverlayElement);
  if (!focusScopeRoot.hasAttribute('tabindex')) {
    focusScopeRoot.setAttribute('tabindex', '-1');
  }

  const weekSelectorInitialFocus = getWeekSelectorInitialFocus(
    calendarOverlayElement,
  );
  if (weekSelectorInitialFocus) {
    weekSelectorInitialFocus.focus();
    return;
  }

  const focusableElements = getFocusableElements(focusScopeRoot);
  if (focusableElements.length > 0) {
    const selectedOrActiveDay = calendarOverlayElement.querySelector<HTMLElement>(
      '.rmdp-day.rmdp-selected, .rmdp-day.rmdp-today, .rmdp-day:not(.rmdp-day-hidden)',
    );
    if (selectedOrActiveDay && isVisible(selectedOrActiveDay)) {
      selectedOrActiveDay.focus();
      return;
    }
    focusableElements[0].focus();
    return;
  }
  focusScopeRoot.focus();
};

export const trapActiveDatePickerOverlayFocus = () => {
  let focusRetryTimeout: number | null = null;
  let calendarMutationObserver: MutationObserver | null = null;

  const attemptInitialFocus = (retriesLeft = 12) => {
    const calendarOverlayElement = getActiveCalendarOverlay();
    if (calendarOverlayElement) {
      focusOverlay(calendarOverlayElement);
      if (!calendarMutationObserver) {
        calendarMutationObserver = new MutationObserver(() => {
          ensureKeyboardTabbableElements(calendarOverlayElement);
        });
        calendarMutationObserver.observe(calendarOverlayElement, {
          childList: true,
          subtree: true,
        });
      }
      return;
    }

    if (retriesLeft <= 0) {
      return;
    }

    focusRetryTimeout = window.setTimeout(() => {
      attemptInitialFocus(retriesLeft - 1);
    }, 30);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const calendarOverlayElement =
      getCalendarOverlayFromNode(event.target) ||
      getCalendarOverlayFromNode(document.activeElement) ||
      getActiveCalendarOverlay();
    if (!calendarOverlayElement) {
      return;
    }

    ensureKeyboardTabbableElements(calendarOverlayElement);

    if (event.key !== 'Tab') {
      return;
    }

    const focusScopeRoot = getFocusScopeRoot(calendarOverlayElement);
    const focusableElements = getFocusableElements(focusScopeRoot);
    event.preventDefault();
    event.stopPropagation();

    const activeElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const isRightChevronFocused = Boolean(
      activeElement && activeElement.closest('.rmdp-arrow-container.rmdp-right'),
    );

    if (!event.shiftKey && isRightChevronFocused) {
      if (focusFirstElementInActivePickerPanel(calendarOverlayElement)) {
        return;
      }
    }

    if (focusableElements.length === 0) {
      focusScopeRoot.focus();
      return;
    }

    const nextFocusableElement = getNextFocusableElement(
      focusableElements,
      activeElement && focusScopeRoot.contains(activeElement)
        ? activeElement
        : null,
      event.shiftKey,
    );

    if (nextFocusableElement) {
      nextFocusableElement.focus();
    }
  };

  const handleFocusIn = (event: FocusEvent) => {
    const calendarOverlayElement =
      getCalendarOverlayFromNode(event.target) ||
      getCalendarOverlayFromNode(document.activeElement) ||
      getActiveCalendarOverlay();
    if (!calendarOverlayElement) {
      return;
    }
    const focusScopeRoot = getFocusScopeRoot(calendarOverlayElement);
    if (event.target instanceof Node && focusScopeRoot.contains(event.target)) {
      return;
    }

    ensureKeyboardTabbableElements(calendarOverlayElement);
    const focusableElements = getFocusableElements(focusScopeRoot);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
      return;
    }
    focusScopeRoot.focus();
  };

  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('focusin', handleFocusIn, true);
  attemptInitialFocus();

  return () => {
    if (focusRetryTimeout) {
      clearTimeout(focusRetryTimeout);
    }
    if (calendarMutationObserver) {
      calendarMutationObserver.disconnect();
    }
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('focusin', handleFocusIn, true);
  };
};
